// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing
// ==============================================
// /papers/requests/:rid/shared — Crew-6. What is shared with the skipper and
// until when (days left), change the date, withdraw (asked twice), and who
// opened or downloaded what. Withdrawing tells the skipper (the API emails).
// ==============================================

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { useCrewRequest, useSetCrewRequest } from '../hooks';
import { apiError } from '../apiError';
import { isSharedNow, isWritable } from '../itemDisplay';
import { maxEndDate, validateEndDate } from '../share';
import { accessEndDate, daysBetween, firstName, fmtDay, fmtWhen, passageLabel, today } from '../dates';
import type { CrewRequestView } from '../types';
import { C, ConfirmDialog, CrewShell, ErrorNote, Loading, SecondaryButton, Tick } from '../ui';

const ACTION: Record<string, string> = { view: 'viewed', download: 'downloaded', export: 'used on the crew list' };

export function CrewSharedPage() {
  const { rid = '' } = useParams();
  const q = useCrewRequest(rid);
  const setView = useSetCrewRequest(rid);
  const shares = useQuery({ queryKey: ['papers-me', 'shares'], queryFn: () => api.papersMyShares(), retry: false });
  const [changing, setChanging] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const data = q.data;
  const activity = useMemo(() => {
    if (!data) return [];
    const docs = new Set(data.items.map((i) => i.paper?.id).filter(Boolean) as string[]);
    const opened = (shares.data?.activity ?? []).filter((a) => docs.has(a.documentId));
    const sharedAt = data.items.filter(isSharedNow).map((i) => i.providedAt).filter(Boolean).sort().pop();
    const who = firstName(data.request.requesterName);
    const rows: Array<{ at: string; kind: string; text: string }> = opened.map((a) => {
      const item = data.items.find((i) => i.paper?.id === a.documentId);
      return { at: a.at, kind: a.action, text: `${firstName(a.actorName, who)} ${ACTION[a.action] ?? a.action} your ${(item?.label ?? a.documentTitle).toLowerCase()}` };
    });
    if (sharedAt) {
      const n = data.items.filter(isSharedNow).length;
      rows.push({ at: sharedAt, kind: 'shared', text: `You shared ${n} paper${n === 1 ? '' : 's'}` });
    }
    return rows.sort((a, b) => b.at.localeCompare(a.at));
  }, [data, shares.data]);

  if (q.isLoading) return <CrewShell><Loading /></CrewShell>;
  if (q.error || !data) return <CrewShell back={{ to: `/papers/requests/${rid}`, label: 'Back to the list' }}><ErrorNote>{apiError(q.error, 'This request could not be loaded').message}</ErrorNote></CrewShell>;

  const { request: req, me, items } = data;
  const who = firstName(req.requesterName);
  const shared = items.filter(isSharedNow);
  const ends = [...new Set(shared.map((i) => accessEndDate(i.grant!.expiresAt, req.timezone)))].sort();
  const todayDate = today(req.timezone);
  const passage = passageLabel(req.fromPlace, req.toPlace);

  const run = async (label: string, fn: () => Promise<CrewRequestView>, done: string) => {
    setBusy(true); setError(null); setNotice(null);
    try { setView(await fn()); setNotice(done); shares.refetch(); return true; } catch (err) { setError(apiError(err, `${label} didn’t go through. Try again.`).message); return false; } finally { setBusy(false); }
  };

  const saveDate = async () => {
    const e = validateEndDate(newDate, todayDate);
    if (e) { setError(e); document.getElementById('shared-new-date')?.focus(); return; }
    const ok = await run('Changing the date', async () => {
      let v: CrewRequestView | null = null;
      for (const i of shared) v = await api.crewChangeAccess(rid, i.id, newDate);
      return v as CrewRequestView;
    }, `${who} can see them until ${fmtDay(newDate)}.`);
    if (ok) setChanging(false);
  };

  const withdraw = async () => {
    const ok = await run('Withdrawing', () => api.crewWithdraw(rid), `${who} can’t open them any more. We’ve let ${who} know.`);
    if (ok) setConfirm(false);
  };

  return (
    <CrewShell right={me.name ?? me.email}>
      {shared.length > 0 ? (
        <div className="flex flex-col gap-2.5 pt-2">
          <div className="flex items-center gap-2.5">
            <span className="h-3.5 w-3.5 rounded-full bg-[#34d27a]" aria-hidden="true" />
            <h1 className="m-0 text-2xl font-bold uppercase tracking-[0.06em] text-[#34d27a]">Shared with {who}</h1>
          </div>
          <div className={`text-base ${C.soft}`}>{shared.length} paper{shared.length === 1 ? '' : 's'}{passage ? ` · ${passage}` : ''}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 pt-2">
          <h1 className="m-0 text-[26px] font-bold">Nothing is shared with {who} right now</h1>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>Your papers are still in your own papers. {isWritable(req) ? `You can share them again from the list.` : ''}</p>
        </div>
      )}

      {notice && <p role="status" className="m-0 rounded-xl bg-[rgba(52,210,122,.1)] px-3.5 py-3 text-[15px] font-semibold text-[#6ee7a2]">{notice}</p>}
      <ErrorNote>{error}</ErrorNote>

      {shared.length > 0 && (
        <section className={`${C.card} flex flex-col gap-1.5 p-4`} aria-labelledby="shared-ends">
          <span id="shared-ends" className={C.label}>{who}’s access ends</span>
          {ends.length === 1 ? (
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-mono text-[26px] font-medium">{fmtDay(ends[0])}</span>
              <span className={`font-mono text-[17px] ${C.muted}`}>{daysLeftLabel(daysBetween(todayDate, ends[0]))}</span>
            </div>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {shared.map((i) => { const d = accessEndDate(i.grant!.expiresAt, req.timezone); return <li key={i.id} className="flex justify-between gap-3 text-base"><span>{i.label}</span><span className="font-mono">{fmtDay(d)} · {daysLeftLabel(daysBetween(todayDate, d))}</span></li>; })}
            </ul>
          )}
          {changing ? (
            <div className="flex flex-col gap-2.5 pt-2.5">
              <label htmlFor="shared-new-date" className="text-sm font-semibold text-[#c7d3e6]">New end date{shared.length > 1 ? ' for every paper' : ''}</label>
              <input id="shared-new-date" type="date" min={todayDate} max={maxEndDate(todayDate)} value={newDate} onChange={(e) => setNewDate(e.target.value)} className={C.input} />
              <div className="flex gap-2.5">
                <SecondaryButton className="flex-1" onClick={() => { setChanging(false); setError(null); }}>Cancel</SecondaryButton>
                <SecondaryButton className="flex-1" tone="filled" onClick={() => void saveDate()} disabled={busy}>{busy ? 'Saving…' : 'Save date'}</SecondaryButton>
              </div>
            </div>
          ) : (
            <div className="flex gap-2.5 pt-2.5">
              <SecondaryButton className="flex-1" onClick={() => { setNewDate(ends[ends.length - 1] ?? ''); setChanging(true); setNotice(null); }}>Change date</SecondaryButton>
              <SecondaryButton className="flex-1" tone="danger" onClick={() => setConfirm(true)}>Withdraw now</SecondaryButton>
            </div>
          )}
        </section>
      )}

      {shared.length === 0 && isWritable(req) && (
        <Link to={`/papers/requests/${rid}`} className={`flex min-h-[52px] items-center justify-center rounded-xl bg-[#2563eb] text-[17px] font-bold text-white ${C.focus}`}>Back to the list</Link>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="shared-activity">
        <span id="shared-activity" className={C.label}>Activity</span>
        {shares.error && <ErrorNote>{apiError(shares.error, 'The activity could not be loaded').message}</ErrorNote>}
        {!shares.error && activity.length === 0 && <p className={`m-0 text-[15px] ${C.muted}`}>Nothing yet. Each time {who} opens or downloads a paper, it shows here.</p>}
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {activity.map((a, n) => (
            <li key={`${a.at}-${n}`} className="flex gap-3 text-[15px]">
              {a.kind === 'shared' ? <Tick className="mt-0.5" />
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9fcbff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>}
              <div className="flex flex-col gap-0.5"><span>{a.text}</span><span className={`font-mono text-[13px] ${C.muted}`}>{fmtWhen(a.at)}</span></div>
            </li>
          ))}
        </ul>
      </section>

      <div className={`${C.card} mt-auto flex flex-col gap-1.5 p-4`}>
        <span className="text-base font-semibold">Your papers stay yours</span>
        <span className={`text-sm leading-relaxed ${C.muted}`}>They’re kept in your own Ship’s Papers. We’ll remind you before your passport or insurance expires. Next time a skipper asks, it’s one tap.</span>
        <Link to="/papers" className={`min-h-[44px] self-start py-2 text-[15px] ${C.link}`}>Go to my papers</Link>
      </div>

      {confirm && (
        <ConfirmDialog title={`Withdraw ${who}’s access?`} confirmLabel="Withdraw access" cancelLabel="Keep sharing" danger busy={busy}
          onConfirm={() => void withdraw()} onCancel={() => setConfirm(false)}>
          {who} won’t be able to open {shared.length === 1 ? 'this paper' : `these ${shared.length} papers`} any more, and we’ll let {who} know. Copies {who} already downloaded stay with {who}. You can share again later.
        </ConfirmDialog>
      )}
    </CrewShell>
  );
}

export function daysLeftLabel(days: number): string {
  if (days <= 0) return 'ends tonight';
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
}

export default CrewSharedPage;
